/**
 * MyFlightsStrip — the logged-in person's own flights, pinned above the project outline.
 *
 * Replaced "My Work" (2026-09-02, docs/FLOW_RECONCILIATION_PLAN.md decision 9). A person's work IS
 * the flights they are crewed on — at most two by database rule — so this is the Flight Board's
 * "In the air" list filtered to one person, in the same row idiom (plane attitude, flight tag,
 * project · milestone, hill stage · crew · due), and it stays put while they navigate the outline
 * underneath. Portals into #aj-sc-flights (outline/static.html), so it sits inside the white
 * content area above the zoom title at every zoom level; full-bleed workspaces cover it like they
 * cover the outline.
 *
 * Reads the shared crew index (outline/crew.js) — the same source as every crew chip — and
 * re-renders on the outline-render the index dispatches when it loads or changes. No local fetch.
 */

import { useEffect, useReducer, useState } from 'react'
import { createPortal } from 'react-dom'
import { wf, isEmbedded } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { flightsForMember, crewIndexReady, crewIndexFailed, ensureCrewIndex, refreshCrewIndex } from '../outline/crew.js'
import { normalizeFlight, rankAssignments, isoToday, fmtDueDate } from '../services/work.js'
import { Plane, attitudeFor, flightTag } from './FlightTracker.jsx'
import { hillLabel } from './FlightPlanTab.jsx'

const COLLAPSE_KEY = 'aj:myflights:collapsed'
const BLUE = '#1a6fa8', RED = '#c0392b', AMBER = '#b8540a'

export default function MyFlightsStrip() {
  const [, force] = useReducer(v => v + 1, 0)
  const [collapsed, setCollapsed] = useState(() => { try { return localStorage.getItem(COLLAPSE_KEY) === '1' } catch { return false } })

  useEffect(() => {
    window.addEventListener('outline-render', force)
    return () => window.removeEventListener('outline-render', force)
  }, [])
  useEffect(() => { ensureCrewIndex() }, [])

  const host = typeof document !== 'undefined' ? document.getElementById('aj-sc-flights') : null
  if (!host || isEmbedded()) return null

  const memberId = wf.currentMemberId
  if (!memberId) return null   // resolves async at startup; nothing to pin until then

  const ready = crewIndexReady()
  const today = isoToday()
  const flights = ready ? flightsForMember(memberId) : []
  // Priority first (2026-09-10): a person's FIRST plane leads their strip, whatever the dates say.
  const rows = rankAssignments(flights.map(f => normalizeFlight(f, memberId)), today).slice().sort((a, b) => (a.priority || 3) - (b.priority || 3))
  function toggle() {
    const next = !collapsed
    setCollapsed(next)
    try { localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0') } catch { /* ignore */ }
  }
  // The Flight Board is reached from the SiteNav only, by the seats that have it (Matt,
  // 2026-09-03). This strip never links there — a squadron row zooms to its project instead.
  function go(item) {
    if (item.kind === 'squadron') {
      if (item.projectId && ctx.zoomToProject) {
        if (ctx.closeAllWorkspaces) ctx.closeAllWorkspaces()
        ctx.zoomToProject(item.projectId)
      }
      return
    }
    if (item.zoomType && item.entityId && ctx.zoomToEntity) {
      if (ctx.closeAllWorkspaces) ctx.closeAllWorkspaces()
      ctx.zoomToEntity(item.zoomType, item.entityId, item.projectId)
    }
  }

  const count = rows.length
  const failed = ready && crewIndexFailed()
  const summary = !ready ? 'loading…' : failed ? 'couldn’t load' : count === 0 ? 'not crewed' : count === 1 ? '1 flight' : count + ' flights'

  return createPortal(
    <div style={S.wrap} data-testid="my-flights">
      <div style={S.head}>
        <button type="button" style={S.headBtn} onClick={toggle} title={collapsed ? 'Show my flights' : 'Hide my flights'}>
          <span style={{ ...S.chev, transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>▾</span>
          <span style={S.eyebrow}>My flights</span>
          <span style={S.meta}>{summary}</span>
        </button>
      </div>
      {!collapsed && ready && (
        failed ? (
          <div style={S.empty}>
            Your flights couldn’t be loaded just now — retrying. <button type="button" onClick={() => refreshCrewIndex()} style={S.retry}>Retry</button>
          </div>
        ) : count === 0 ? (
          <div style={S.empty}>
            You're not crewed on anything right now. The tower assigns crews on the Flight Board when a flight launches.
          </div>
        ) : (
          <div style={S.list}>
            {rows.map(item => {
              const flight = flights.find(f => f.flightId === item.id)
              const late = !!(item.due && item.due < today)
              const stalledDays = flight && flight.hillMovedAt ? Math.floor((Date.now() - new Date(flight.hillMovedAt).getTime()) / 86400000) : 0
              const stalled = stalledDays >= 7
              const others = item.crewNames.filter(n => !(flight && (flight.crew || []).some(c => c.memberAirtableId === memberId && c.name === n)))
              return (
                <div key={item.id} style={S.row} onClick={() => go(item)} role="button" tabIndex={0}
                  onKeyDown={e => { if (e.key === 'Enter') go(item) }}
                  title={item.kind === 'squadron' ? 'Open the Flight Board' : 'Open in the outline'}>
                  <Plane deg={attitudeFor(item.hillPosition)} color={stalled ? AMBER : late ? RED : BLUE} size={18}
                    icon={item.kind === 'thread' || item.kind === 'squadron' ? 'heli' : undefined} />
                  <span style={S.tag}>{flightTag({ projectName: item.project, flightNumber: item.flightNumber })}</span>
                  <div style={S.main}>
                    <div style={S.title}>{item.project || 'Project not named'} · {item.label}</div>
                    <div style={S.sub}>
                      {hillLabel(item.hillPosition)}
                      {others.length ? ' · with ' + others.join(', ') : ''}
                      {item.priority === 1 ? ' · your 1st' : item.priority === 2 ? ' · your 2nd' : ''}
                      {item.isCaptain ? ' · captain' : ''}
                      {stalled && <span style={{ color: AMBER }}> · not moved in {stalledDays}d</span>}
                    </div>
                  </div>
                  <span style={{ ...S.due, color: late ? RED : 'var(--aj-text-muted)' }}>{item.due ? fmtDueDate(item.due) : ''}</span>
                </div>
              )
            })}
          </div>
        )
      )}
    </div>,
    host
  )
}

const S = {
  wrap: { margin: '0 0 14px', border: '0.5px solid var(--aj-border)', borderRadius: 10, background: 'var(--aj-bg-surface)', overflow: 'hidden' },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px 6px 8px', background: 'var(--aj-bg-subtle)', borderBottom: '0.5px solid var(--aj-border)' },
  headBtn: { display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', padding: '2px 4px', cursor: 'pointer', color: 'var(--aj-text)' },
  chev: { display: 'inline-block', fontSize: 10, color: 'var(--aj-text-muted)', transition: 'transform .15s ease', width: 10 },
  eyebrow: { fontFamily: '"Montserrat", sans-serif', fontWeight: 600, fontSize: 9.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--aj-text)' },
  meta: { fontFamily: '"DM Mono", monospace', fontSize: 10.5, color: 'var(--aj-text-muted)' },
  list: { display: 'flex', flexDirection: 'column' },
  row: { display: 'flex', alignItems: 'center', gap: 12, padding: '9px 14px', borderBottom: '0.5px solid var(--aj-border)', cursor: 'pointer' },
  tag: { fontFamily: '"DM Mono", monospace', fontSize: 11, fontWeight: 500, color: 'var(--aj-text)', whiteSpace: 'nowrap', minWidth: 78 },
  main: { flex: '1 1 auto', minWidth: 0 },
  title: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  sub: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  due: { fontFamily: '"DM Mono", monospace', fontSize: 11, whiteSpace: 'nowrap', minWidth: 46, textAlign: 'right' },
  empty: { padding: '12px 14px', fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text-muted)', lineHeight: 1.5 },
  retry: { background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--aj-accent)', font: 'inherit', fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 2 },
}
